import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import DataTable from '../../components/common/DataTable';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { formatDateTime, formatDate } from '../../lib/utils';
import { AlertTriangle, FileText, Download, Eye, CheckCircle, XCircle, Plus, ImagePlus, X, Image } from 'lucide-react';
import { toast } from 'sonner';

const IncidentsPage = () => {
  const { api, isAdmin, isHouseManager, isFamily } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  
  // Both admin and house manager can review incidents
  const canReview = isAdmin || isHouseManager;

  // Edit draft state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newIncident, setNewIncident] = useState({
    participant_id: "",
    house_id: "",
    category: "",
    severity: "low",
    description: "",
    immediate_actions: "",
    injuries: "",
    witnesses: ""
  });
  const [participants, setParticipants] = useState([]);
  const [houses, setHouses] = useState([]);
  const [editingIncident, setEditingIncident] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [incidentImages, setIncidentImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    fetchIncidents();
    const fetchFormData = async () => {
      try {
        const [pRes, hRes] = await Promise.all([
          api.get("/participants"),
          api.get("/houses")
        ]);
        setParticipants(pRes.data || []);
        setHouses(hRes.data || []);
      } catch (e) {}
    };
    fetchFormData();
  }, [statusFilter]);

  const fetchIncidents = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await api.get('/incidents', { params });
      setIncidents(res.data);
    } catch (error) {
      toast.error('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (incidentId, newStatus) => {
    try {
      await api.put(`/incidents/${incidentId}/status`, null, {
        params: { new_status: newStatus, admin_notes: adminNotes }
      });
      toast.success(`Incident ${newStatus}`);
      setReviewDialogOpen(false);
      setAdminNotes('');
      fetchIncidents();
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to update incident status");
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setSelectedImages(prev => [...prev, ...newImages]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const uploadImagesToIncident = async (incidentId) => {
    if (selectedImages.length === 0) return;
    setUploadingImages(true);
    try {
      for (const img of selectedImages) {
        const formData = new FormData();
        formData.append("file", img.file);
        await api.post(`/incidents/${incidentId}/images`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      setSelectedImages([]);
    } catch (error) {
      toast.error("Failed to upload images");
    } finally {
      setUploadingImages(false);
    }
  };

  const fetchIncidentImages = async (incidentId) => {
    try {
      const res = await api.get(`/incidents/${incidentId}/images`);
      setIncidentImages(res.data || []);
    } catch (e) {
      setIncidentImages([]);
    }
  };

  const getErrorMessage = (error) => {
    const detail = error.response?.data?.detail;
    if (!detail) return "An error occurred";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(", ");
    return "An error occurred";
  };

  const handleCreateIncident = async (asDraft = false) => {
    if (!newIncident.participant_id || !newIncident.house_id || !newIncident.category || !newIncident.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const incidentData = {
        participant_id: newIncident.participant_id,
        house_id: newIncident.house_id,
        category: newIncident.category,
        severity: newIncident.severity,
        description: newIncident.description,
        immediate_actions: newIncident.immediate_actions || null,
        injuries: newIncident.injuries || null,
        witnesses: newIncident.witnesses ? newIncident.witnesses.split(",").map(w => w.trim()).filter(w => w) : [],
      };
      const response = await api.post("/incidents", incidentData);
      if (selectedImages.length > 0 && response.data?.id) {
        await uploadImagesToIncident(response.data.id);
      }
      toast.success(asDraft ? "Draft saved" : "Incident reported successfully");
      setCreateDialogOpen(false);
      setNewIncident({ participant_id: "", house_id: "", category: "", severity: "low", description: "", immediate_actions: "", injuries: "", witnesses: "" });
      setSelectedImages([]);
      fetchIncidents();
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to create incident");
    }
  };

  const handleEditDraft = (incident) => {
    setEditingIncident({
      id: incident.id,
      description: incident.description || "",
      immediate_actions: incident.immediate_actions || "",
      injuries: incident.injuries || "",
      witnesses: incident.witnesses || ""
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingIncident) return;
    try {
      await api.put(`/incidents/${editingIncident.id}`, editingIncident);
      toast.success("Draft updated successfully");
      setEditDialogOpen(false);
      setEditingIncident(null);
      fetchIncidents();
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to update draft");
    }
  };

  const handleSubmitEdit = async () => {
    if (!editingIncident) return;
    try {
      await api.put(`/incidents/${editingIncident.id}`, editingIncident);
      await api.put(`/incidents/${editingIncident.id}/submit`);
      toast.success("Incident submitted successfully");
      setEditDialogOpen(false);
      setEditingIncident(null);
      fetchIncidents();
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to submit incident");
    }
  };

  const handleDownloadPDF = async (incidentId) => {
    try {
      const response = await api.get(`/incidents/${incidentId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incident_${incidentId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const columns = [
    {
      header: 'Date',
      cell: (row) => formatDate(row.created_at)
    },
    {
      header: 'Participant',
      accessor: 'participant_name'
    },
    {
      header: 'Category',
      accessor: 'category'
    },
    {
      header: 'Severity',
      cell: (row) => (
        <span className={`font-medium ${
          row.severity === 'High' ? 'text-red-500' : 
          row.severity === 'Medium' ? 'text-amber-500' : 'text-green-500'
        }`}>
          {row.severity}
        </span>
      )
    },
    {
      header: 'Reporter',
      accessor: 'reporter_name'
    },
    {
      header: 'Status',
      cell: (row) => <StatusBadge status={row.status} />
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIncident(row);
              fetchIncidentImages(row.id);
              setReviewDialogOpen(true);
            }}
            data-testid={`view-incident-${row.id}`}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {row.status === "draft" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleEditDraft(row);
              }}
              data-testid={`edit-draft-${row.id}`}
            >
              Edit
            </Button>
          )}
          {(row.status === 'approved' || row.status === 'published') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadPDF(row.id);
              }}
              data-testid={`download-incident-${row.id}`}
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="incidents-page">
      <PageHeader 
        title="Incident Reports"
        description="View and manage incident reports across all houses"
        actions={
          <Button onClick={() => setCreateDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="h-4 w-4 mr-2" />
            Report Incident
          </Button>
        }
      />

      {/* Filters */}
      <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Status Filter</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="incident-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="under_review">Under Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Incidents Table */}
      <DataTable 
        columns={columns}
        data={incidents}
        emptyMessage="No incidents found"
      />

      {/* Review/View Incident Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Incident Report
            </DialogTitle>
          </DialogHeader>
          {selectedIncident && (
            <div className="space-y-6 py-4">
              <div className="flex items-center justify-between">
                <StatusBadge status={selectedIncident.status} />
                <span className="text-sm text-muted-foreground">
                  ID: {selectedIncident.id.substring(0, 8).toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Participant</Label>
                  <p className="font-medium">{selectedIncident.participant_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">House</Label>
                  <p className="font-medium">{selectedIncident.house_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Reporter</Label>
                  <p className="font-medium">{selectedIncident.reporter_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Date/Time</Label>
                  <p className="font-medium">{formatDateTime(selectedIncident.created_at)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Category</Label>
                  <p className="font-medium">{selectedIncident.category}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Severity</Label>
                  <p className={`font-medium ${
                    selectedIncident.severity === 'High' ? 'text-red-500' : 
                    selectedIncident.severity === 'Medium' ? 'text-amber-500' : 'text-green-500'
                  }`}>
                    {selectedIncident.severity}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">Description</Label>
                <p className="mt-1 p-3 rounded-lg bg-muted/30">{selectedIncident.description}</p>
              </div>

              {selectedIncident.immediate_actions && (
                <div>
                  <Label className="text-muted-foreground">Immediate Actions Taken</Label>
                  <p className="mt-1 p-3 rounded-lg bg-muted/30">{selectedIncident.immediate_actions}</p>
                </div>
              )}

              {selectedIncident.witnesses?.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Witnesses</Label>
                  <p className="mt-1">{selectedIncident.witnesses.join(', ')}</p>
                </div>
              )}

              {selectedIncident.admin_notes && (
                <div>
                  <Label className="text-muted-foreground">Admin Notes</Label>
                  <p className="mt-1 p-3 rounded-lg bg-primary/10">{selectedIncident.admin_notes}</p>
                </div>
              )}

              {canReview && ['submitted', 'under_review'].includes(selectedIncident.status) && (
                <div className="space-y-4 pt-4 border-t border-border/40">
                  <div className="space-y-2">
                    <Label>Review Notes</Label>
                    <Textarea 
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add notes for this incident..."
                      rows={3}
                      data-testid="admin-notes"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Incident Images */}
          {incidentImages.length > 0 && (
            <div className="space-y-2 border-t pt-4">
              <Label className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                Attached Photos ({incidentImages.length})
              </Label>
              <div className="flex flex-wrap gap-2">
                {incidentImages.map((img) => (
                  <a
                    key={img.id}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/incidents/images/${img.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-24 h-24 rounded-lg overflow-hidden border hover:border-primary transition-colors"
                  >
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/incidents/images/${img.id}`}
                      alt={img.filename}
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {canReview && selectedIncident && (
              <>
                {selectedIncident.status === 'submitted' && (
                  <Button 
                    variant="outline"
                    onClick={() => handleStatusChange(selectedIncident.id, 'under_review')}
                    data-testid="mark-under-review"
                  >
                    Mark Under Review
                  </Button>
                )}
                {['submitted', 'under_review'].includes(selectedIncident.status) && (
                  <Button 
                    onClick={() => handleStatusChange(selectedIncident.id, 'approved')}
                    data-testid="approve-incident"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                )}
                {selectedIncident.status === 'approved' && isAdmin && (
                  <Button 
                    onClick={() => handleStatusChange(selectedIncident.id, 'published')}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="publish-incident"
                  >
                    Publish to Family
                  </Button>
                )}
              </>
            )}
            {(selectedIncident?.status === 'approved' || selectedIncident?.status === 'published') && (
              <Button 
                variant="outline"
                onClick={() => handleDownloadPDF(selectedIncident.id)}
                data-testid="download-pdf"
              >
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Incident Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Report New Incident</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Participant *</Label>
                <Select value={newIncident.participant_id} onValueChange={(v) => setNewIncident({...newIncident, participant_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select participant" /></SelectTrigger>
                  <SelectContent>
                    {participants.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>House *</Label>
                <Select value={newIncident.house_id} onValueChange={(v) => setNewIncident({...newIncident, house_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select house" /></SelectTrigger>
                  <SelectContent>
                    {houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={newIncident.category} onValueChange={(v) => setNewIncident({...newIncident, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="injury">Injury</SelectItem>
                    <SelectItem value="medication">Medication Error</SelectItem>
                    <SelectItem value="behavior">Behavioral</SelectItem>
                    <SelectItem value="property">Property Damage</SelectItem>
                    <SelectItem value="near_miss">Near Miss</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select value={newIncident.severity} onValueChange={(v) => setNewIncident({...newIncident, severity: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea value={newIncident.description} onChange={(e) => setNewIncident({...newIncident, description: e.target.value})} rows={4} placeholder="Describe what happened..." />
            </div>
            <div className="space-y-2">
              <Label>Immediate Actions Taken</Label>
              <Textarea value={newIncident.immediate_actions} onChange={(e) => setNewIncident({...newIncident, immediate_actions: e.target.value})} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Injuries</Label>
              <Textarea value={newIncident.injuries} onChange={(e) => setNewIncident({...newIncident, injuries: e.target.value})} rows={2} />
            </div>

            {/* Image Upload Section */}
            <div className="space-y-2 border-t pt-4">
              <Label className="flex items-center gap-2">
                <ImagePlus className="h-4 w-4" />
                Add Photos
              </Label>
              <div className="flex flex-wrap gap-2">
                {selectedImages.map((img, idx) => (
                  <div key={idx} className="relative w-20 h-20">
                    <img src={img.preview} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(idx)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors">
                  <ImagePlus className="h-6 w-6 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Add</span>
                  <input type="file" accept="image/*" multiple onChange={handleImageSelect} className="hidden" />
                </label>
              </div>
              <p className="text-xs text-muted-foreground">Photos help document the incident</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button variant="secondary" onClick={() => handleCreateIncident(true)}>Save Draft</Button>
            <Button onClick={() => handleCreateIncident(false)} className="bg-red-600 hover:bg-red-700">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Draft Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit Draft Incident</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editingIncident?.description || ""}
                onChange={(e) => setEditingIncident({...editingIncident, description: e.target.value})}
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label>Immediate Actions Taken</Label>
              <Textarea
                value={editingIncident?.immediate_actions || ""}
                onChange={(e) => setEditingIncident({...editingIncident, immediate_actions: e.target.value})}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Injuries</Label>
              <Textarea
                value={editingIncident?.injuries || ""}
                onChange={(e) => setEditingIncident({...editingIncident, injuries: e.target.value})}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Witnesses</Label>
              <Textarea
                value={editingIncident?.witnesses || ""}
                onChange={(e) => setEditingIncident({...editingIncident, witnesses: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button variant="secondary" onClick={handleSaveEdit}>Save Draft</Button>
            <Button onClick={handleSubmitEdit}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncidentsPage;
